import { ValidationItem } from './validation.utils';

/**
 * The minimal shape of a repositioned field the highlight logic reads. Matches
 * the entries in a `GetIngestionDocumentFragment.fields` record (keyed by
 * contract document uuid) — `id` is the field uuid, `mappingKey` links it to a
 * validation result, and `pageNumber` scopes page-specific failures.
 */
export interface RepositionedFieldLike {
  id?: string | null;
  mappingKey?: string | null;
  pageNumber?: number | null;
  x?: number | null;
  y?: number | null;
  width?: number | null;
  height?: number | null;
}

/** A document's repositioned fields, keyed by contract document uuid. */
export type FieldsByContractDocument = Record<string, RepositionedFieldLike[]>;

const asFieldArray = (value: unknown): RepositionedFieldLike[] =>
  Array.isArray(value) ? (value as RepositionedFieldLike[]) : [];

/**
 * Canonical field id: repositioned fields carry an `{originalId}-repositioned`
 * id, but the PDF overlay rects (and validation clicks) key on the original id.
 * Both the field-highlight sets and `buildValidationFieldsMap` MUST normalize
 * the same way, or `errorFieldIds`/`successFieldIds` won't match the boxes.
 */
const normalizeFieldId = (id: string): string =>
  id.replace(/-repositioned$/, '');

/** A single field box CompliancePDFViewer's `validationFieldsMap` expects. */
export interface ValidationField {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
  pageNumber: number;
  contractDocumentUuid: string;
}

/**
 * Groups a document's repositioned fields (keyed by contract document uuid)
 * into the `Map<pageNumber, ValidationField[]>` CompliancePDFViewer draws its
 * box overlays from. Strips the `-repositioned` id suffix (a no-op for ids that
 * don't carry it) and skips fields without an id or page.
 *
 * Single source of truth shared by the compliance drawer, the embedded SkySlope
 * drawer, and the agent compliance review page — each previously built this map
 * inline.
 */
export function buildValidationFieldsMap(
  fields: FieldsByContractDocument | null | undefined
): Map<number, ValidationField[]> {
  const map = new Map<number, ValidationField[]>();
  if (!fields) {
    return map;
  }

  for (const [contractDocumentUuid, contractDocumentFields] of Object.entries(
    fields
  )) {
    for (const field of asFieldArray(contractDocumentFields)) {
      const id = field.id == null ? undefined : normalizeFieldId(field.id);
      if (!id || field.pageNumber == null) {
        continue;
      }
      const list = map.get(field.pageNumber) ?? [];
      list.push({
        id,
        x: field.x ?? 0,
        y: field.y ?? 0,
        width: field.width ?? 0,
        height: field.height ?? 0,
        pageNumber: field.pageNumber,
        contractDocumentUuid,
      });
      map.set(field.pageNumber, list);
    }
  }

  return map;
}

/**
 * The field ids a single validation item targets: fields whose `mappingKey` is
 * one of the item's `mappingKeys`, scoped to the item's contract document (when
 * set) and — when the item has page attribution — to those failing pages.
 *
 * This mirrors the field-matching the compliance drawer
 * (`ComplianceDrawer.tsx`) and the extension's `useDocumentValidationIssues`
 * run; it's extracted here so every surface (including the agent compliance
 * review page) shares one implementation.
 */
export function getValidationItemFieldIds(
  item: ValidationItem,
  fields: FieldsByContractDocument | null | undefined
): string[] {
  if (!fields) {
    return [];
  }

  const mappingKeys = new Set(item.mappingKeys ?? []);
  if (mappingKeys.size === 0) {
    return [];
  }

  const failingPages = item.pageNumbers?.length
    ? new Set(item.pageNumbers)
    : null;

  // Scope to the item's contract document when known, else search all of them.
  const scopedEntries =
    item.contractDocumentUuid != null
      ? [
          [
            item.contractDocumentUuid,
            fields[item.contractDocumentUuid],
          ] as const,
        ]
      : Object.entries(fields);

  const ids = new Set<string>();
  for (const [, contractDocumentFields] of scopedEntries) {
    for (const field of asFieldArray(contractDocumentFields)) {
      if (field.id == null || field.mappingKey == null) {
        continue;
      }
      if (!mappingKeys.has(field.mappingKey)) {
        continue;
      }
      if (
        failingPages &&
        (field.pageNumber == null || !failingPages.has(field.pageNumber))
      ) {
        continue;
      }
      // Normalize the same way buildValidationFieldsMap does, so these ids
      // match the PDF overlay rects' ids.
      ids.add(normalizeFieldId(field.id));
    }
  }

  return Array.from(ids);
}

/** Field-highlight sets the PDF viewer consumes, derived from validation items. */
export interface ComplianceFieldHighlights {
  /** Field ids to paint as failed (errors). */
  errorFieldIds: string[];
  /** Field ids to paint as passed (successes), with errors removed. */
  successFieldIds: string[];
  /** Per-field message for failed fields (the callout text). */
  fieldMessages: Record<string, string>;
}

/**
 * Builds the error/success field id sets + per-field error messages from the
 * split validation items (the output of `getValidationResultsForDocument`) and
 * the document's repositioned fields. Error wins: a field flagged by both a
 * failed and a passed result is only an error.
 */
export function buildComplianceFieldHighlights(
  errors: ValidationItem[],
  successes: ValidationItem[],
  fields: FieldsByContractDocument | null | undefined
): ComplianceFieldHighlights {
  const errorFieldIds = new Set<string>();
  const fieldMessages: Record<string, string> = {};
  for (const error of errors) {
    for (const fieldId of getValidationItemFieldIds(error, fields)) {
      errorFieldIds.add(fieldId);
      if (fieldMessages[fieldId] == null) {
        fieldMessages[fieldId] = error.message;
      }
    }
  }

  const successFieldIds = new Set<string>();
  for (const success of successes) {
    for (const fieldId of getValidationItemFieldIds(success, fields)) {
      if (!errorFieldIds.has(fieldId)) {
        successFieldIds.add(fieldId);
      }
    }
  }

  return {
    errorFieldIds: Array.from(errorFieldIds),
    successFieldIds: Array.from(successFieldIds),
    fieldMessages,
  };
}
